// src/pages/markets/board/card/EntryMarkerLayer.tsx
//
// HTML, not SVG: the chart scales through preserveAspectRatio="none", so a
// circle drawn inside it would arrive as an ellipse.

import { Box, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import type { EntryMarker } from '../utils/entryMarkers';
import type { SideLabels } from '../utils/boardOutcomeLabels';
import { sideColor } from '../utils/sideColor';
import { VB, xForTime, yForValue, type Geometry } from './trendGeometry';
import { formatCents, formatDuration, formatUsd } from './cardFormat';
import { HoverPanel } from './HoverPanel';
import { EntryMarkerPanel } from './EntryMarkerPanel';

export const ENTRY_MARKER_TEST_ID = 'board-card-entry-marker';

const DOT_SIZE = 6;
const HIT_SIZE = 16;
// A chart holds up to eight of these. Without a dwell, a cursor crossing the
// chart — or the list scrolling under a still cursor — opens every panel it
// passes and fetches a profile and a position for each.
const OPEN_DELAY_MS = 180;
// Reserved so the panel is the same rectangle empty, loading and loaded.
const PANEL_MIN_WIDTH = 288;
const PANEL_MIN_HEIGHT = 120;

// The card surface folds itself on click and only skips a click it sees
// already prevented, so the dot has to prevent as well as stop.
function stopCardNav(e: React.MouseEvent) {
    e.preventDefault();
    e.stopPropagation();
}

export interface EntryMarkerLayerProps {
    geo: Geometry;
    markers: EntryMarker[];
    chipText: string | null;
    labels: SideLabels;
    marketId: string;
    eventId: string;
    now: number;
}

function markerTitle(m: EntryMarker, labels: SideLabels): string {
    const side = m.side === 'NO' ? labels.no.text : labels.yes.text;
    return `${formatUsd(m.capitalUsd)} ${side} · entered ${formatCents(m.entryPrice)} · held ${formatDuration(m.heldSeconds)}`;
}

export function EntryMarkerLayer({
    geo,
    markers,
    chipText,
    labels,
    marketId,
    eventId,
    now,
}: EntryMarkerLayerProps) {
    const theme = useTheme();
    if (markers.length === 0 && !chipText) return null;

    return (
        <Box sx={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
            {markers.map(m => {
                const left = (xForTime(geo, m.enteredAtMs) / VB) * 100;
                const top = (yForValue(geo, m.yesPrice) / VB) * 100;
                const color = sideColor(labels, m.side, theme);
                return (
                    // One element is the hit area, the hover target and the
                    // click guard: the card surface folds on any click it does
                    // not see prevented, and a near-miss on the dot is common.
                    <HoverPanel
                        key={`${m.address}:${m.outcomeIndex}`}
                        testId={ENTRY_MARKER_TEST_ID}
                        placement="right-start"
                        padded={false}
                        openDelayMs={OPEN_DELAY_MS}
                        minWidth={PANEL_MIN_WIDTH}
                        minHeight={PANEL_MIN_HEIGHT}
                        onClick={stopCardNav}
                        content={() => (
                            <EntryMarkerPanel
                                marker={m}
                                labels={labels}
                                marketId={marketId}
                                eventId={eventId}
                                now={now}
                            />
                        )}
                        style={{ left: `${left}%`, top: `${top}%` }}
                        sx={{
                            position: 'absolute',
                            width: HIT_SIZE,
                            height: HIT_SIZE,
                            transform: 'translate(-50%, -50%)',
                            pointerEvents: 'auto',
                            cursor: 'default',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <Box
                            title={markerTitle(m, labels)}
                            sx={{
                                width: DOT_SIZE,
                                height: DOT_SIZE,
                                borderRadius: '50%',
                                bgcolor: color,
                                boxShadow: `0 0 0 2px ${theme.palette.background.default}`,
                            }}
                        />
                    </HoverPanel>
                );
            })}

            {chipText && (
                <Typography
                    sx={{
                        position: 'absolute',
                        // Right, not left: the earliest entries sit at the left
                        // edge and the last-value badge is off on this chart.
                        right: 0,
                        top: 0,
                        fontSize: '10px',
                        color: 'text.secondary',
                        bgcolor: alpha(theme.palette.background.default, 0.85),
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: '3px',
                        px: 0.5,
                        pointerEvents: 'auto',
                        cursor: 'default',
                    }}
                    onClick={stopCardNav}
                >
                    {chipText}
                </Typography>
            )}
        </Box>
    );
}
